/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { useGetEventTypesQuery, useGetEventSubTypesQuery } from "../../store/services/events-types";
import { useGetEventsQuery } from "../../store/services/events";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useDebounce } from "../../hooks/useDebounce";

interface UserSegmentationFiltersProps {
  // Datos de usuario
  email?: string;
  setEmail?: (value: string) => void;
  firstName?: string;
  setFirstName?: (value: string) => void;
  lastName?: string;
  setLastName?: (value: string) => void;
  birthDateMonth?: number;
  setBirthDateMonth?: (value: number | undefined) => void;
  birthDateDay?: number;
  setBirthDateDay?: (value: number | undefined) => void;

  // Datos de comportamiento
  eventIds?: number[];
  setEventIds?: (value: number[]) => void;
  eventTypeId?: number;
  setEventTypeId?: (value: number | undefined) => void;
  eventSubTypeId?: number;
  setEventSubTypeId?: (value: number | undefined) => void;
  behaviorType?: 'registered' | 'saved' | 'both';
  setBehaviorType?: (value: 'registered' | 'saved' | 'both' | undefined) => void;

  // Datos de organización
  tenantId?: string;
  setTenantId?: (value: string | undefined) => void;

  // Callback para aplicar filtros
  onApplyFilters?: () => void;
}

export const UserSegmentationFilters: React.FC<UserSegmentationFiltersProps> = ({
  email,
  setEmail = () => {},
  firstName,
  setFirstName = () => {},
  lastName,
  setLastName = () => {},
  birthDateMonth,
  setBirthDateMonth = () => {},
  birthDateDay,
  setBirthDateDay = () => {},
  eventIds,
  setEventIds = () => {},
  eventTypeId,
  setEventTypeId = () => {},
  eventSubTypeId,
  setEventSubTypeId = () => {},
  behaviorType,
  setBehaviorType = () => {},
  tenantId,
  setTenantId = () => {},
  onApplyFilters = () => {},
}) => {
  const catalogQuery = tenantId ? { tenantId } : undefined;
  const { data: eventTypes, isLoading: isLoadingEventTypes } = useGetEventTypesQuery(catalogQuery);
  const { data: eventSubTypes, isLoading: isLoadingEventSubTypes } =
    useGetEventSubTypesQuery(catalogQuery);

  const changeTenant = (nextTenantId: string | undefined) => {
    if (nextTenantId === tenantId) return;
    setTenantId(nextTenantId);
    setEventTypeId(undefined);
    setEventSubTypeId(undefined);
  };
  
  const [eventSearch, setEventSearch] = React.useState<string>("");
  const debouncedEventSearch = useDebounce(eventSearch, 500);

  const shouldFetchEvents = Boolean(debouncedEventSearch);

  const { data: eventsPage, isLoading: isLoadingEvents } = useGetEventsQuery(
    shouldFetchEvents ? { name: debouncedEventSearch, limit: 50 } : { name: "", limit: 1 },
    { skip: !shouldFetchEvents }
  );
  const events = eventsPage?.events;

  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});

  const eventSubTypeOptions = React.useMemo(() => {
    if (!eventSubTypes) return [];
    if (!eventTypeId) return eventSubTypes;
    return eventSubTypes.filter((subType) => subType.eventTypeId === eventTypeId);
  }, [eventSubTypes, eventTypeId]);

  const tenantOptions = React.useMemo(() => {
    return tenants || [];
  }, [tenants]);

  const behaviorTypeOptions = [
    { label: "Inscripto", value: "registered" },
    { label: "Guardado", value: "saved" },
    { label: "Ambos", value: "both" }
  ];

  const monthOptions = [
    { label: "Enero", value: 1 },
    { label: "Febrero", value: 2 },
    { label: "Marzo", value: 3 },
    { label: "Abril", value: 4 },
    { label: "Mayo", value: 5 },
    { label: "Junio", value: 6 },
    { label: "Julio", value: 7 },
    { label: "Agosto", value: 8 },
    { label: "Septiembre", value: 9 },
    { label: "Octubre", value: 10 },
    { label: "Noviembre", value: 11 },
    { label: "Diciembre", value: 12 }
  ];

  const clearAllFilters = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setBirthDateMonth(undefined);
    setBirthDateDay(undefined);
    setEventIds([]);
    setEventTypeId(undefined);
    setEventSubTypeId(undefined);
    setBehaviorType(undefined);
    setTenantId(undefined);
    onApplyFilters();
  };

  return (
    <div className="grid">
      {/* Sección: Datos de Usuario */}
      <div className="col-12">
        <h3 className="text-lg font-semibold mb-2">Datos de Usuario</h3>
      </div>
      
      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <label htmlFor="email">Email</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => setEmail("")}
        />
      </div>

      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="firstName"
            name="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.currentTarget.value)}
          />
          <label htmlFor="firstName">Nombre</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => setFirstName("")}
        />
      </div>

      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="lastName"
            name="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.currentTarget.value)}
          />
          <label htmlFor="lastName">Apellido</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => setLastName("")}
        />
      </div>

      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="birthDateMonth"
            name="birthDateMonth"
            value={birthDateMonth}
            onChange={(e: DropdownChangeEvent) => setBirthDateMonth(e.value)}
            options={monthOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Mes de nacimiento"
            showClear
          />
          <label htmlFor="birthDateMonth">Mes de nacimiento</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => setBirthDateMonth(undefined)}
        />
      </div>

      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <InputNumber
            className="w-full"
            id="birthDateDay"
            name="birthDateDay"
            value={birthDateDay}
            onValueChange={(e) => setBirthDateDay(e.value ?? undefined)}
            min={1}
            max={31}
            showButtons
            placeholder="Día de nacimiento"
          />
          <label htmlFor="birthDateDay">Día de nacimiento</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => setBirthDateDay(undefined)}
        />
      </div>

      {/* Sección: Datos de Comportamiento */}
      <div className="col-12 mt-3">
        <h3 className="text-lg font-semibold mb-2">Datos de Comportamiento</h3>
      </div>

      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="behaviorType"
            name="behaviorType"
            value={behaviorType}
            onChange={(e: DropdownChangeEvent) => setBehaviorType(e.value)}
            options={behaviorTypeOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Tipo de comportamiento"
            showClear
          />
          <label htmlFor="behaviorType">Tipo de comportamiento</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => setBehaviorType(undefined)}
        />
      </div>

      <div className="col-12 md:col-8 p-inputgroup">
        <span className="p-float-label w-full">
          <MultiSelect
            className="w-full"
            id="eventIds"
            name="eventIds"
            value={eventIds}
            onChange={(e) => setEventIds(e.value)}
            options={events || []}
            optionLabel="name"
            optionValue="id"
            placeholder="Seleccionar eventos"
            loading={isLoadingEvents}
            filter
            onFilter={(e) => setEventSearch(e.filter)}
            showClear
            display="chip"
          />
          <label htmlFor="eventIds">Eventos</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => {
            setEventIds([]);
            setEventSearch("");
          }}
        />
      </div>

      <div className="col-12 md:col-6 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="eventTypeId"
            name="eventTypeId"
            value={eventTypeId}
            onChange={(e: DropdownChangeEvent) => {
              setEventTypeId(e.value);
              setEventSubTypeId(undefined);
            }}
            options={eventTypes || []}
            optionLabel="eventTypeName"
            optionValue="eventTypeId"
            placeholder="Seleccionar categoría"
            loading={isLoadingEventTypes}
            showClear
          />
          <label htmlFor="eventTypeId">Categoría</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => {
            setEventTypeId(undefined);
            setEventSubTypeId(undefined);
          }}
        />
      </div>

      <div className="col-12 md:col-6 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="eventSubTypeId"
            name="eventSubTypeId"
            value={eventSubTypeId}
            onChange={(e: DropdownChangeEvent) => setEventSubTypeId(e.value)}
            options={eventSubTypeOptions}
            optionLabel="eventSubTypeName"
            optionValue="eventSubTypeId"
            placeholder="Seleccionar subcategoría"
            loading={isLoadingEventSubTypes}
            disabled={!eventTypeId}
            showClear
          />
          <label htmlFor="eventSubTypeId">Subcategoría</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => setEventSubTypeId(undefined)}
        />
      </div>

      {/* Sección: Datos de Organización */}
      <div className="col-12 mt-3">
        <h3 className="text-lg font-semibold mb-2">Datos de Organización</h3>
      </div>

      <div className="col-12 md:col-6 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="tenantId"
            name="tenantId"
            value={tenantId}
            onChange={(e: DropdownChangeEvent) => changeTenant(e.value ?? undefined)}
            options={tenantOptions}
            optionLabel="provider"
            optionValue="id"
            placeholder="Seleccionar tenant"
            loading={isLoadingTenants}
            showClear
          />
          <label htmlFor="tenantId">Tenant</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => changeTenant(undefined)}
        />
      </div>

      {/* Botones de acción */}
      <div className="col-12 flex justify-content-end gap-2 mt-3">
        <Button
          label="Limpiar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          onClick={clearAllFilters}
        />
        <Button
          label="Aplicar filtros"
          icon="pi pi-filter"
          onClick={onApplyFilters}
        />
      </div>
    </div>
  );
};
